import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { ArrowLeft, Printer } from "lucide-react";

/**
 * Welcome-board poster — A4 printout of the universal check-in link as a QR
 * code. Two variants: the reception board (light) and the gate sign (huge
 * type, light-on-black, readable from a car window at night).
 */
const AdminCheckInPoster = () => {
  const navigate = useNavigate();
  const url = `${window.location.origin}/check-in`;
  const [variant, setVariant] = useState("gate");

  return (
    <div className="min-h-screen bg-[#F5EFE6]" data-testid="checkin-poster-page">
      {/* Toolbar — hidden when printing */}
      <div className="no-print flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-[#0F0E0E]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-[#F5EFE6] text-[10px] font-brand tracking-[0.16em] uppercase hover:text-[#E4B2A8]"
          data-testid="poster-back"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <div className="flex items-center gap-2">
          {[["gate", "Gate sign (night)"], ["reception", "Reception board"]].map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setVariant(v)}
              className={`px-4 py-2 rounded-full text-[10px] font-brand tracking-[0.14em] uppercase transition ${
                variant === v ? "bg-[#B76E79] text-white" : "text-[#F5EFE6]/70 hover:text-[#F5EFE6] border border-[#F5EFE6]/25"
              }`}
              data-testid={`poster-variant-${v}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#B76E79] text-white hover:bg-[#96525E] transition text-[10px] font-brand tracking-[0.14em] uppercase"
          data-testid="poster-print"
        >
          <Printer size={13} /> Print A4
        </button>
      </div>

      {variant === "gate" ? (
        /* GATE SIGN — huge type on black, readable from a car at night */
        <div className="flex justify-center py-8">
          <div
            className="gate-poster text-center px-8 py-12 flex flex-col items-center"
            style={{ width: "min(680px, 92vw)", background: "#000000", borderRadius: 18 }}
            data-testid="checkin-poster-gate"
          >
            <p className="font-serif text-2xl tracking-[0.3em] uppercase" style={{ color: "#F5EFE6" }}>Walnut Lakes</p>

            <h1 className="font-serif leading-none mt-8" style={{ color: "#FFFFFF", fontSize: "clamp(56px, 9vw, 88px)" }}>
              CHECK IN<br />HERE
            </h1>
            <p className="mt-5 font-semibold" style={{ color: "#FFBF00", fontSize: "clamp(18px, 3vw, 26px)", letterSpacing: "0.06em" }}>
              SCAN WITH YOUR PHONE — 2 MINUTES
            </p>

            <div className="mt-8 bg-white rounded-3xl" style={{ padding: 26 }} data-testid="poster-qr">
              <QRCode value={url} size={280} fgColor="#000000" />
            </div>

            <p className="mt-7 font-mono font-bold" style={{ color: "#FFFFFF", fontSize: "clamp(16px, 2.6vw, 22px)", wordBreak: "break-all" }}>
              {url.replace("https://", "")}
            </p>

            <div className="w-24 h-px my-8" style={{ background: "#B76E79" }} />

            <p style={{ color: "#F5EFE6", fontSize: "clamp(15px, 2.2vw, 19px)", lineHeight: 1.5 }} className="max-w-md">
              The barrier opens once you're checked in —<br />reception can see you arrive.
            </p>
            <p className="mt-5 font-bold" style={{ color: "#FFFFFF", fontSize: "clamp(16px, 2.4vw, 21px)", letterSpacing: "0.08em" }}>
              NO SIGNAL? CALL 07982 844201
            </p>
          </div>
        </div>
      ) : (
        /* RECEPTION BOARD — the original light A4 poster */
        <div className="flex justify-center py-8">
          <div
            className="print-sheet-body bg-white border border-[#DDD1BE] rounded-2xl shadow-sm text-center px-10 py-12 flex flex-col items-center"
            style={{ width: "min(680px, 92vw)" }}
            data-testid="checkin-poster"
          >
          <p className="font-serif text-3xl text-[#14100F] tracking-[0.18em] uppercase">Walnut Lakes</p>
          <p className="text-[10px] tracking-[0.26em] uppercase text-[#6A5F58] mt-1.5">Holiday Park · Lincolnshire</p>

          <div className="w-16 h-px bg-[#B76E79] my-7" />

          <h1 className="font-serif text-4xl text-[#14100F] leading-tight">Arriving today?<br />Skip the queue.</h1>
          <p className="text-sm text-[#3A322E] mt-4 max-w-md leading-relaxed">
            Scan to check in on your phone — it takes about two minutes and makes your arrival quicker.
          </p>

          <div className="mt-8 bg-white p-5 rounded-2xl border-2 border-[#0F0E0E]" data-testid="poster-qr">
            <QRCode value={url} size={220} fgColor="#0F0E0E" />
          </div>
          <p className="mt-4 text-xs font-mono text-[#3A322E] tracking-wide">{url}</p>

          <div className="mt-8 grid grid-cols-3 gap-4 w-full text-left">
            {[
              ["1", "Scan the code with your phone camera"],
              ["2", "Tell us your name, arrival date and lodge or pitch"],
              ["3", "Add your party, fishing and vehicle — done"],
            ].map(([n, text]) => (
              <div key={n} className="border border-[#EFE6D8] rounded-xl p-3.5">
                <p className="font-serif text-xl text-[#B76E79]">{n}</p>
                <p className="text-[11px] text-[#3A322E] mt-1 leading-snug">{text}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 text-[11px] text-[#6A5F58] leading-relaxed">
            Prefer to check in with us? Just come to reception — we're happy to help.<br />
            Main Road, Algarkirk, Boston PE20 2LQ · 07982 844201 · Justina@walnutlakes.co.uk
          </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCheckInPoster;
